// The ONE qrcode seam (R-072/D-075): renders a QR for a value on a canvas,
// themed from the live tokens. Consumers pass the encoded value only — the
// label-printing pipeline (sizes, quiet zones per format) is W-6 material.
import { useEffect, useRef } from 'preact/hooks';
import QRCode from 'qrcode';
import { colors } from '../../tokens/colors.stylex';

/** Resolve a StyleX token (`var(--x…)`) to its current computed value. */
function resolveToken(el: HTMLElement, token: string): string {
  const name = /var\((--[^),\s]+)/.exec(token)?.[1];
  if (!name) return token;
  return getComputedStyle(el).getPropertyValue(name).trim() || token;
}

interface QrCodeProps {
  value: string;
  size?: number;
}

export function QrCode({ value, size = 128 }: QrCodeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const draw = () => {
      const dark = resolveToken(canvas, colors.text as unknown as string) || '#000000';
      const light = resolveToken(canvas, colors.surface as unknown as string) || '#ffffff';
      QRCode.toCanvas(canvas, value, {
        width: size,
        margin: 1,
        color: { dark, light },
      }).catch(() => {
        /* a failed render leaves the canvas blank — nothing to recover */
      });
    };
    draw();
    const observer = new MutationObserver(draw);
    observer.observe(document.documentElement, { attributes: true });
    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, [value, size]);

  return <canvas ref={canvasRef} aria-label={value} role="img" />;
}
